import { useState } from "react";
import { Button, Space, Upload, message } from "antd";
import { PaperClipOutlined, UploadOutlined } from "@ant-design/icons";
import {
  AVISO_COMPROVANTE,
  ComprovantePayload,
  FORMATOS_COMPROVANTE_ACEITOS,
  LIMITE_COMPROVANTE_BYTES,
  lerComprovante,
} from "@/utils/comprovante";

type Props = {
  /** Arquivo já escolhido (ainda não enviado), se houver. */
  comprovante?: ComprovantePayload | null;
  onChange: (comprovante: ComprovantePayload | null) => void;
  disabled?: boolean;
  rotulo?: string;
  /** Esconde a linha de aviso quando o rótulo já está num Form.Item com ajuda. */
  mostrarAviso?: boolean;
  size?: "small" | "middle" | "large";
};

/**
 * Escolha do comprovante de PIX/Transferência para enviar junto com o payload
 * que cria a parcela. O arquivo fica só no formulário até o salvar — anexar
 * depois, numa parcela que já existe, é pela tela de Controle Pix/Transferência.
 */
const ComprovanteUpload: React.FC<Props> = ({
  comprovante,
  onChange,
  disabled = false,
  rotulo = "Anexar comprovante",
  mostrarAviso = true,
  size = "middle",
}) => {
  const [lendo, setLendo] = useState(false);

  const selecionar = async (arquivo: File) => {
    if (arquivo.size > LIMITE_COMPROVANTE_BYTES) {
      message.warning("O comprovante deve ter no máximo 10 MB.");
      return;
    }

    setLendo(true);
    try {
      onChange(await lerComprovante(arquivo));
    } catch {
      message.error("Não foi possível ler o arquivo escolhido.");
    } finally {
      setLendo(false);
    }
  };

  if (comprovante) {
    return (
      <Space size="small" wrap>
        <span>
          <PaperClipOutlined /> {comprovante.nomeArquivo}
        </span>
        {!disabled && (
          <Button type="link" size="small" danger onClick={() => onChange(null)}>
            Remover
          </Button>
        )}
      </Space>
    );
  }

  return (
    <>
      <Upload
        accept={FORMATOS_COMPROVANTE_ACEITOS}
        showUploadList={false}
        disabled={disabled}
        beforeUpload={(arquivo) => {
          selecionar(arquivo as File);
          // o envio é feito pelo payload do formulário, não pelo antd
          return false;
        }}
      >
        <Button icon={<UploadOutlined />} loading={lendo} disabled={disabled} size={size}>
          {rotulo}
        </Button>
      </Upload>
      {mostrarAviso && <div className="text-xs opacity-70 mt-1">{AVISO_COMPROVANTE}</div>}
    </>
  );
};

export default ComprovanteUpload;
